import { IDENTITY_PROMPT } from './identity';
import { REASONING_PROMPT } from './reasoning';
import { GUARDRAILS_PROMPT } from './guardrails';
import { FORMATTING_PROMPT } from './formatting';

export interface PromptContext {
  industry?: string;
  goal?: string;
  article?: string;
  pageUrl?: string;
  knowledgeChunks?: KnowledgeChunk[];
  graphPaths?: GraphPathNode[];
}

export interface KnowledgeChunk {
  title: string;
  summary: string;
  entityType: string;
  slug: string;
  relevanceScore: number;
}

export interface GraphPathNode {
  entityType: string;
  entityId: string;
  title: string;
  relationship: string;
  depth: number;
}

function buildKnowledgeContext(chunks: KnowledgeChunk[]): string {
  if (!chunks || chunks.length === 0) return '';

  const formattedChunks = chunks
    .map((chunk) => `- **${chunk.title}** (${chunk.entityType}): ${chunk.summary}`)
    .join('\n');

  return `\n## Available Knowledge Context\nThe following verified knowledge is available to reference in your response:\n${formattedChunks}\n\nUse this knowledge to ground your response. Cite specific items when relevant.`;
}

function buildGraphContext(paths: GraphPathNode[]): string {
  if (!paths || paths.length === 0) return '';

  const grouped = paths.reduce<Record<string, GraphPathNode[]>>((acc, node) => {
    const key = node.entityType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(node);
    return acc;
  }, {});

  const sections = Object.entries(grouped)
    .map(([type, nodes]) => {
      const items = nodes
        .map((n) => `  - ${n.title} (via "${n.relationship}", depth ${n.depth})`)
        .join('\n');
      return `- **${type}**:\n${items}`;
    })
    .join('\n');

  return `\n## Knowledge Graph Context\nThe following entities are connected in the knowledge graph and may inform your response:\n${sections}\n\nUse these connections to provide richer, more contextual answers.`;
}

function buildPageContext(context: PromptContext): string {
  const parts: string[] = [];

  if (context.industry) {
    parts.push(`The user is currently exploring the **${context.industry}** industry.`);
  }
  if (context.goal) {
    parts.push(`The user is interested in the business goal: **${context.goal}**.`);
  }
  if (context.article) {
    parts.push(`The user was reading the article: **${context.article}**.`);
  }

  if (parts.length === 0) return '';

  return `\n## Current Context\n${parts.join(' ')}`;
}

export function assembleSystemPrompt(context: PromptContext): string {
  const sections = [
    IDENTITY_PROMPT,
    REASONING_PROMPT,
    buildPageContext(context),
    buildKnowledgeContext(context.knowledgeChunks || []),
    buildGraphContext(context.graphPaths || []),
    GUARDRAILS_PROMPT,
    FORMATTING_PROMPT,
  ];

  return sections.filter(Boolean).join('\n\n---\n\n');
}
